import React, { useState, useEffect, useRef, useLayoutEffect } from 'react'
import { useNavigate } from 'react-router'
import { useFetchAuthorisation, useFetchUserData } from '../../utils/AppFunctions'
import config from '../../app-config'
import { get_plan_by_plan_id, create_new_plan, update_plan, delete_plan, get_all_plan_components, update_plan_components, update_plan_sections } from '../../api/planAPI'
import 'react-toastify/dist/ReactToastify.css';
import AdminLayout from '../../layouts/AdminLayout'
import notify from '../../utils/IANotification';
import Switch from "react-switch";
import { MdAdd, MdDelete, MdArrowBack, MdSave, MdOutlineArtTrack, MdArticle } from 'react-icons/md';
import TextComponent from '../../components/plan/TextComponent';
import VerseComponent from '../../components/plan/VerseComponent';
import WebComponent from '../../components/plan/WebComponent';
import ImageComponent from '../../components/plan/ImageComponent';
import AudioComponent from '../../components/plan/AudioComponent';
import DocumentComponent from '../../components/plan/DocumentComponent';
import VideoComponent from '../../components/plan/VideoComponent';
import FileUploadField from '../../components/FileUploadField';
import SubNavbar from '../../components/ui/SubNavbar';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';

export default function PlanFormPage() {
  const navigate = useNavigate()
  useFetchAuthorisation("user")
  useFetchUserData()
  let plan_id = new URLSearchParams(window.location.search).get("plan_id")

  const [userHeaderHeight, setUserHeaderHeight] = useState(document?.getElementById("user-header")?.clientHeight)
  const [planHeaderHeight, setPlanHeaderHeight] = useState(document?.getElementById("plan-component")?.clientHeight)

  useLayoutEffect(() => {
    setUserHeaderHeight(document?.getElementById("user-header")?.clientHeight)
    setPlanHeaderHeight(document?.getElementById("plan-component")?.clientHeight)
  }, [userHeaderHeight, planHeaderHeight])

  const [activeTab, setActiveTab] = useState("general")
  const [planData, setPlanData] = useState({
    plan_name: "",
    plan_description: "",
    media_asset_id: "",
    frequency: "1",
    is_published: "0",
  })
  const [loaded, setLoaded] = useState(false)
  const [isPreview, setIsPreview] = useState(false)
  const [previousSave, setPreviousSave] = useState()

  const [sectionData, setSectionData] = useState([])
  const [planComponentsData, setPlanComponentsData] = useState([])
  const [selectedSection, setSelectedSection] = useState(0)

  const tabs = [
    { id: 'general', label: 'General Info' },
    { id: 'sections', label: 'Sections & Builder' },
    { id: 'overview', label: 'Content Overview' },
  ]

  const fetchPlanById = async () => {
    if (plan_id && plan_id !== "new") {
      const data = await get_plan_by_plan_id(plan_id)
      if (data?.status === 200) {
        setPlanData(data?.data)
      } else {
        notify({ type: "error", message: "Failed to fetch plan data" })
      }
    }
  }

  const createPlan = async () => {
    const data = await create_new_plan(planData)
    if (data?.status === 200) {
      notify({ type: "success", message: "Plan created successfully" })
      navigate("/admin/plan/details?plan_id=" + data.data.id)
    } else {
      notify({ type: "error", message: "Failed to create plan" })
    }
  }

  const updatePlan = async () => {
    if (!plan_id || plan_id === "new") return
    const data = await update_plan(plan_id, planData)
    if (data?.status === 200) {
      notify({ type: "success", message: "Plan updated successfully" })
    } else {
      notify({ type: "error", message: "Failed to update plan" })
    }
  }

  const handleFormSubmit = (e) => {
    if (e) e.preventDefault()
    if (plan_id && plan_id !== "new") {
      updatePlan()
      saveComponents()
      saveSection()
    } else {
      createPlan()
    }
  }

  const deletePlan = async () => {
    if (!plan_id || plan_id === "new") return
    const data = await delete_plan(plan_id)
    if (data?.status === 200) {
      notify({ type: "success", message: "Plan deleted successfully" })
      navigate("/admin/plan")
    } else {
      notify({ type: "error", message: "Failed to delete plan" })
    }
  }

  const fetchPlanComponents = async () => {
    setLoaded(false)
    if (!plan_id || plan_id === "new") return
    const data = await get_all_plan_components(plan_id)
    if (data?.status === 200) {
      setPlanComponentsData(data.data.components || [])
      setSectionData(data.data.sections || [])
      if (!selectedSection?.id && data.data.sections?.length > 0) setSelectedSection(data.data.sections[0])
    } else {
      notify({ type: "error", message: "Failed to fetch plan components" })
    }
    setLoaded(true)
  }

  const saveComponents = async (temp) => {
    if (!plan_id) return
    const data = await update_plan_components({
      components: temp ? temp : planComponentsData,
      section: sectionData,
      plan_id: plan_id,
    })
    if (data?.status === 200) {
      fetchPlanComponents()
      notify({ type: "success", message: "Components saved successfully" })
    } else {
      notify({ type: "error", message: "Failed to save components" })
    }
  }

  const saveSection = async (temp) => {
    if (!loaded) return
    if (JSON.stringify(sectionData) === previousSave) return
    const data = await update_plan_sections({
      section: temp ? temp : sectionData,
      plan_id: plan_id,
    })
    if (data?.status === 200) {
      setPreviousSave(JSON.stringify(sectionData))
      fetchPlanComponents()
      notify({ type: "success", message: "Sections saved successfully" })
    } else {
      notify({ type: "error", message: "Failed to save sections" })
    }
  }

  useEffect(() => {
    fetchPlanById()
    fetchPlanComponents()
  }, [])

  return (
    <AdminLayout
      navSelected={"Plans & Materials"}
      headerText={plan_id === "new" ? "New Discipleship Plan" : "Edit Discipleship Plan"}
      toolbar={
        <>
          <div className="flex items-center gap-3">
            <Button variant="outline" icon={MdArrowBack} onClick={() => navigate('/admin/plan')}>
              Back
            </Button>
            <h1 className='text-xl font-bold text-gray-900'>
              {plan_id === "new" ? "Create Discipleship Plan" : planData.plan_name || "Edit Plan"}
            </h1>
          </div>
          <div className='flex gap-2'>
            {plan_id && plan_id !== "new" && (
              <Button variant="danger" icon={MdDelete} onClick={deletePlan}>
                Delete
              </Button>
            )}
            <Button variant="primary" icon={MdSave} onClick={handleFormSubmit}>
              {plan_id === "new" ? "Create" : "Save Changes"}
            </Button>
          </div>
        </>
      }
    >
      <SubNavbar
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {/* TAB 1: GENERAL INFO */}
      {activeTab === "general" && (
        <Card title="Plan Profile &amp; Cover" subtitle="Configure plan name, description, frequency, and cover graphic.">
          {plan_id !== "new" && (
            <div className="mb-6">
              <FileUploadField
                onChange={() => {}}
                upload_url={`${config.endpoint}/file/plan/cover/id/` + plan_id}
                max_files={1}
                max_file_size={5}
                text={"Upload Plan Cover Photo"}
                subtext={"Upload cover graphic for discipleship plan"}
              />
            </div>
          )}

          <div className="space-y-6">
            <div className="input-container">
              <label className="input-label font-semibold">Plan Name</label>
              <input
                type="text"
                className="text-input"
                value={planData.plan_name || ""}
                placeholder='e.g. Road to Obedience'
                onChange={(e) => setPlanData({ ...planData, plan_name: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="input-container">
                <label className="input-label font-semibold">Publish Frequency</label>
                <select
                  className="select-input"
                  value={planData.frequency || "1"}
                  onChange={(e) => setPlanData({ ...planData, frequency: e.target.value })}
                >
                  <option value={1}>Daily</option>
                  <option value={7}>Weekly</option>
                  <option value={30}>Monthly</option>
                </select>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
                <div>
                  <label className="font-semibold text-sm text-gray-900">Publication Status</label>
                  <p className="text-xs text-gray-500">Publish plan for member access.</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs font-semibold text-gray-700">
                    {planData.is_published === "1" ? "Published" : "Draft"}
                  </span>
                  <Switch
                    checked={planData.is_published === "1"}
                    onChange={(checked) => setPlanData({ ...planData, is_published: checked ? "1" : "0" })}
                  />
                </div>
              </div>
            </div>

            <div className="input-container">
              <label className="input-label font-semibold">Plan Description</label>
              <textarea
                className="textarea-input h-32"
                value={planData.plan_description || ""}
                placeholder='e.g. This plan is designed to help you grow in your faith...'
                onChange={(e) => setPlanData({ ...planData, plan_description: e.target.value })}
              />
            </div>
          </div>
        </Card>
      )}

      {/* TAB 2: SECTIONS & BUILDER */}
      {activeTab === "sections" && (
        <Card
          title="Plan Builder &amp; Components"
          subtitle="Add sections and interactive learning components."
          headerRight={
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-gray-700">Preview Mode</span>
                <Switch
                  checked={isPreview}
                  onChange={(checked) => setIsPreview(checked)}
                />
              </div>
              <Button
                variant="secondary"
                size="sm"
                icon={MdOutlineArtTrack}
                onClick={() => {
                  saveSection([...sectionData, {
                    plan_id: plan_id,
                    section_name: `Section ${sectionData.length + 1}`,
                  }])
                }}
              >
                Add Section
              </Button>
            </div>
          }
        >
          {plan_id === "new" ? (
            <p className="text-xs text-gray-400 italic py-6 text-center">Save the plan profile first before adding interactive sections.</p>
          ) : (
            <div className="space-y-6">
              {/* Section Pills */}
              <div className="flex gap-3 overflow-x-auto pb-2 border-b border-gray-100">
                {sectionData.map((section, sectionIndex) => {
                  const isSel = selectedSection?.id === section.id
                  return (
                    <button
                      key={sectionIndex}
                      type="button"
                      onClick={() => setSelectedSection(section)}
                      className={`px-4 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap border ${
                        isSel
                          ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                          : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100"
                      }`}
                    >
                      {section.section_name}
                    </button>
                  )
                })}
              </div>

              {/* Component Editor for Selected Section */}
              {selectedSection?.id ? (
                <div className="space-y-6">
                  {planComponentsData.map((component, index) => {
                    if (component.section_id != selectedSection.id) return null
                    let component_id = planComponentsData.filter((c) => c.section_id == selectedSection.id).findIndex((c) => c.id == component.id) + 1

                    return (
                      <div key={index} className="p-5 bg-gray-50 rounded-2xl border border-gray-200 space-y-4">
                        {!isPreview && (
                          <div className="flex items-center justify-between border-b border-gray-200 pb-3">
                            <span className="font-bold text-xs text-gray-900">Component #{component_id}</span>
                            <Button
                              variant="danger"
                              size="sm"
                              icon={MdDelete}
                              onClick={() => {
                                let newComponents = [...planComponentsData]
                                newComponents.splice(index, 1)
                                setPlanComponentsData(newComponents)
                              }}
                            >
                              Delete
                            </Button>
                          </div>
                        )}

                        {!isPreview && (
                          <div className="input-container max-w-xs">
                            <label className="input-label font-semibold">Component Type</label>
                            <select
                              className="select-input"
                              value={component.content_type || "verse"}
                              onChange={(e) => {
                                let newComponents = [...planComponentsData]
                                newComponents[index].content_type = e.target.value
                                setPlanComponentsData(newComponents)
                              }}
                            >
                              <option value="verse">Verse</option>
                              <option value="rich-text">Rich Text</option>
                              <option value="image">Image</option>
                              <option value="video">Video</option>
                              <option value="audio">Audio</option>
                              <option value="web-link">Embedded HTML</option>
                              <option value="document">Document</option>
                            </select>
                          </div>
                        )}

                        {component?.content_type === "rich-text" && (
                          <TextComponent
                            readOnly={isPreview}
                            data={component?.json_content}
                            onChange={(newContent) => {
                              let newComponents = [...planComponentsData]
                              newComponents[index].json_content = newContent
                              setPlanComponentsData(newComponents)
                            }}
                          />
                        )}
                        {component?.content_type === "verse" && (
                          <VerseComponent
                            readOnly={isPreview}
                            data={component?.json_content}
                            onChange={(newContent) => {
                              let newComponents = [...planComponentsData]
                              newComponents[index].json_content = newContent
                              setPlanComponentsData(newComponents)
                            }}
                          />
                        )}
                        {component?.content_type === "web-link" && (
                          <WebComponent
                            readOnly={isPreview}
                            data={component}
                            onChangeUrl={(newContent) => {
                              let newComponents = [...planComponentsData]
                              newComponents[index].content = newContent
                              setPlanComponentsData(newComponents)
                            }}
                          />
                        )}
                        {component?.content_type === "image" && (
                          <ImageComponent
                            readOnly={isPreview}
                            id={index}
                            data={component}
                            onChangeUrl={(newContent) => {
                              let newComponents = [...planComponentsData]
                              newComponents[index].content = newContent
                              setPlanComponentsData(newComponents)
                            }}
                            onChangeFile={(newContent) => {
                              let newComponents = [...planComponentsData]
                              newComponents[index].media_asset_id = newContent
                              setPlanComponentsData(newComponents)
                            }}
                          />
                        )}
                        {component?.content_type === "video" && (
                          <VideoComponent
                            readOnly={isPreview}
                            id={index}
                            data={component}
                            onChangeUrl={(newContent) => {
                              let newComponents = [...planComponentsData]
                              newComponents[index].content = newContent
                              setPlanComponentsData(newComponents)
                            }}
                            onChangeFile={(newContent) => {
                              let newComponents = [...planComponentsData]
                              newComponents[index].media_asset_id = newContent
                              setPlanComponentsData(newComponents)
                            }}
                          />
                        )}
                        {component?.content_type === "audio" && (
                          <AudioComponent
                            readOnly={isPreview}
                            id={index}
                            data={component}
                            onChangeUrl={(newContent) => {
                              let newComponents = [...planComponentsData]
                              newComponents[index].content = newContent
                              setPlanComponentsData(newComponents)
                            }}
                            onChangeFile={(newContent) => {
                              let newComponents = [...planComponentsData]
                              newComponents[index].media_asset_id = newContent
                              setPlanComponentsData(newComponents)
                            }}
                          />
                        )}
                        {component?.content_type === "document" && (
                          <DocumentComponent
                            readOnly={isPreview}
                            id={index}
                            data={component}
                            onChangeUrl={(newContent) => {
                              let newComponents = [...planComponentsData]
                              newComponents[index].content = newContent
                              setPlanComponentsData(newComponents)
                            }}
                            onChangeFile={(newContent) => {
                              let newComponents = [...planComponentsData]
                              newComponents[index].media_asset_id = newContent
                              setPlanComponentsData(newComponents)
                            }}
                          />
                        )}
                      </div>
                    )
                  })}

                  <div className="pt-2">
                    <Button
                      variant="primary"
                      icon={MdAdd}
                      onClick={() => {
                        let newComponents = [...planComponentsData]
                        newComponents.push({
                          section_id: selectedSection.id,
                          plan_id: plan_id,
                          content_type: "verse",
                          is_question: "0",
                          question_type: "",
                          content: "",
                          json_content: "",
                          question_options: "",
                        })
                        saveComponents(newComponents)
                      }}
                    >
                      Add Component to {selectedSection.section_name}
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-gray-400 italic text-center py-6">Select a section above to edit components.</p>
              )}
            </div>
          )}
        </Card>
      )}

      {/* TAB 3: CONTENT OVERVIEW */}
      {activeTab === "overview" && (
        <Card title="Plan Outline &amp; Content Structure" subtitle="Tree structure overview of sections and components.">
          {sectionData?.length === 0 ? (
            <p className="text-xs text-gray-400 italic text-center py-6">No sections created yet.</p>
          ) : (
            <div className="space-y-4">
              {sectionData?.map((section, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-2">
                  <div className="flex items-center gap-2 font-bold text-sm text-gray-900">
                    <MdOutlineArtTrack size={16} className="text-blue-500" />
                    <span>{section?.section_name}</span>
                  </div>
                  <div className="pl-6 space-y-1.5 border-l-2 border-gray-200 ml-2">
                    {planComponentsData
                      ?.filter(c => c.section_id == section.id)
                      .map((c, cIdx) => (
                        <div key={cIdx} className="flex items-center gap-2 text-xs text-gray-600">
                          <MdArticle size={14} className="text-gray-400" />
                          <span className="capitalize font-semibold">{c.content_type}</span>
                        </div>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </AdminLayout>
  )
}
